import { useEffect, useState } from "react";
import { api, formatRupiah, dispDT } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, FileImage, Wallet } from "lucide-react";
import { PageLoader } from "@/components/PageLoader";

export default function EmployeeKasbon() {
  const [limit, setLimit] = useState(null);
  const [list, setList] = useState([]);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [proofBlob, setProofBlob] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = () =>
    Promise.all([
      api.get("/kasbon/limit").then((r) => setLimit(r.data)),
      api.get("/kasbon/mine").then((r) => setList(r.data)),
    ]);

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/kasbon/request", { amount: parseFloat(amount), note });
      toast.success("Kasbon diajukan");
      setAmount("");
      setNote("");
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal");
    } finally {
      setBusy(false);
    }
  };

  const openProof = async (path) => {
    setProofBlob(null);
    try {
      const r = await api.get(`/files/proof`, { params: { path }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      setProofBlob({ url, type: r.data.type });
    } catch {
      toast.error("Gagal memuat bukti");
    }
  };

  return (
    <div className="px-4 py-6 max-w-md mx-auto space-y-5">
      <div>
        <p className="overline mb-1">Pinjaman</p>
        <h1 className="font-display text-2xl font-bold tracking-tight">Kasbon</h1>
        <p className="text-sm text-zinc-500 mt-1">Ajukan kasbon dan lihat riwayatnya.</p>
      </div>

      {loading && <PageLoader label="Memuat data kasbon..." />}
      {!loading && (
      <>
      {limit && (
        <Card className="p-4 border border-zinc-200 shadow-none">
          <p className="overline mb-2">Limit Bulan Ini</p>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><p className="text-xs text-zinc-500">Gaji</p><p className="font-semibold">{formatRupiah(limit.salary)}</p></div>
            <div><p className="text-xs text-zinc-500">Persen</p><p className="font-semibold">{limit.pct}%</p></div>
            <div><p className="text-xs text-zinc-500">Limit</p><p className="font-semibold">{formatRupiah(limit.limit)}</p></div>
            <div><p className="text-xs text-zinc-500">Terpakai</p><p className="font-semibold text-amber-700">{formatRupiah(limit.used)}</p></div>
            <div className="col-span-2 pt-2 border-t border-zinc-100">
              <p className="overline">Sisa</p>
              <p className="font-display text-xl font-bold" data-testid="kasbon-remaining">{formatRupiah(limit.remaining)}</p>
            </div>
          </div>
        </Card>
      )}

      {limit?.eligibility && !limit.eligibility.allowed && (
        <Card className="p-4 border-2 border-red-300 bg-gradient-to-br from-red-50 to-red-100 shadow-none" data-testid="kasbon-locked-banner">
          <div className="flex items-start gap-2">
            <span className="text-2xl">🔒</span>
            <div className="min-w-0 flex-1">
              <p className="font-display font-bold text-red-900">Kasbon Terkunci</p>
              <p className="text-xs text-red-800 mt-1 leading-relaxed">{limit.eligibility.reason}</p>
              {limit.eligibility.prev_score !== null && (
                <p className="text-[0.65rem] text-red-700 mt-2 font-mono">
                  Skor bulan {limit.eligibility.prev_month}: {limit.eligibility.prev_score} / minimum {limit.eligibility.threshold}
                </p>
              )}
            </div>
          </div>
        </Card>
      )}

      <Card className="p-4 border border-zinc-200 shadow-none">
        <p className="overline mb-3">Ajukan Kasbon</p>
        {limit && !limit.can_request && limit.eligibility?.allowed && (
          <div className="mb-3 p-3 rounded-sm bg-amber-50 border border-amber-300 text-sm text-amber-900" data-testid="kasbon-blocked">
            <p className="font-semibold">⚠ Tidak Bisa Mengajukan</p>
            <p className="text-xs mt-0.5">{limit.reason}</p>
          </div>
        )}
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Nominal</Label>
            <Input type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} required data-testid="input-kasbon-amount" disabled={limit && !limit.can_request} />
          </div>
          <div>
            <Label>Catatan</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} required data-testid="input-kasbon-note" disabled={limit && !limit.can_request} />
          </div>
          <Button type="submit" disabled={busy || (limit && !limit.can_request)} className="w-full bg-primary hover:bg-primary/90" data-testid="btn-submit-kasbon">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Wallet className="w-4 h-4 mr-2" />Ajukan</>}
          </Button>
        </form>
      </Card>

      <div className="space-y-2">
        <p className="overline">Riwayat</p>
        {list.length === 0 && <p className="text-sm text-zinc-500">Belum ada kasbon.</p>}
        {list.map((k) => (
          <Card key={k.id} className="p-3 border border-zinc-200 shadow-none" data-testid={`kasbon-${k.id}`}>
            <div className="flex justify-between">
              <div>
                <p className="font-semibold">{formatRupiah(k.amount)}</p>
                <p className="text-xs text-zinc-500">{dispDT(k.requested_at)}</p>
                <p className="text-xs text-zinc-600 mt-1">{k.note}</p>
                {k.admin_note && <p className="text-xs text-zinc-500 mt-1">Catatan admin: {k.admin_note}</p>}
              </div>
              <StatusBadge status={k.status} />
            </div>
            {k.proof_file && (
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm" className="mt-2" onClick={() => openProof(k.proof_file)} data-testid={`btn-view-proof-${k.id}`}>
                    <FileImage className="w-4 h-4 mr-1.5" /> Lihat Bukti
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader><DialogTitle>Bukti Transfer</DialogTitle></DialogHeader>
                  {!proofBlob && <p className="text-sm text-zinc-500">Memuat...</p>}
                  {proofBlob?.type?.startsWith("image/") && (
                    <img src={proofBlob.url} alt="proof" className="max-h-[70vh] object-contain w-full" />
                  )}
                  {proofBlob?.type === "application/pdf" && (
                    <iframe src={proofBlob.url} className="w-full h-[70vh]" title="proof" />
                  )}
                </DialogContent>
              </Dialog>
            )}
          </Card>
        ))}
      </div>
      </>
      )}
    </div>
  );
}

const StatusBadge = ({ status }) => {
  const map = {
    pending: ["bg-amber-100 text-amber-800 border-amber-300", "Pending"],
    approved: ["bg-green-100 text-green-800 border-green-300", "Disetujui"],
    rejected: ["bg-red-100 text-red-800 border-red-300", "Ditolak"],
  };
  const [cls, label] = map[status] || ["", status];
  return <Badge className={`${cls} border h-fit`} variant="outline">{label}</Badge>;
};
